.button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 96px;
  height: 32px;
  padding: 0 10px;
  color: var(--holo-button-text, var(--color-text-default, #fff));
  font: inherit;
  font-size: 14px;
  line-height: 22px;
  white-space: nowrap;
  cursor: pointer;
  border: 0;
  border-radius: var(--button-radius, 6px);
  outline: none;
  transition:
    color 0.16s ease,
    background-color 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    opacity 0.16s ease;
}

.button:focus-visible {
  box-shadow:
    0 0 0 2px rgba(122, 124, 255, 0.2),
    var(--holo-button-shadow, none);
}

.button:disabled {
  cursor: not-allowed;
}

.size-sm {
  min-width: auto;
  height: 28px;
  padding: 0 9px;
  font-size: 12px;
  line-height: 20px;
}

.size-md {
  min-width: 96px;
  height: 32px;
}

.block {
  width: 100%;
}

.iconOnly {
  width: 36px;
  min-width: 36px;
  padding: 0;
}

.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
}

.icon svg {
  display: block;
  width: 16px;
  height: 16px;
}

.label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.loadingIndicator {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 999px;
  animation: holo-button-spin 0.8s linear infinite;
}

.loading {
  pointer-events: none;
}

.variant-primary,
.variant-icon {
  --holo-button-shadow: 0 3px 1.5px rgba(26, 26, 26, 0.3);

  color: var(--color-text-default, #fff);
  background: var(--color-primary-default, #7b7cff);
  box-shadow: var(--holo-button-shadow);
}

.variant-primary:hover:not(.disabled),
.variant-icon:hover:not(.disabled) {
  background: var(--color-primary-hover, #96a1ff);
}

.variant-primary:active:not(.disabled),
.variant-icon:active:not(.disabled) {
  background: var(--color-primary-active, #686aff);
}

.variant-ghost {
  color: var(--color-text-default, #fff);
  background: var(--gohst-color, rgba(26, 35, 67, 0.48));
}

.variant-ghost:hover:not(.disabled) {
  background: rgba(36, 47, 87, 0.72);
}

.variant-ghost:active:not(.disabled) {
  background: rgba(20, 28, 57, 0.9);
}

.variant-outline {
  color: var(--color-text-brand, #7b7cff);
  background: transparent;
  border: 1px solid var(--color-primary-default, #7b7cff);
}

.variant-outline:hover:not(.disabled) {
  color: var(--color-primary-hover, #96a1ff);
  border-color: var(--color-primary-hover, #96a1ff);
}

.variant-outline:active:not(.disabled) {
  color: var(--color-primary-active, #686aff);
  border-color: var(--color-primary-active, #686aff);
}

.variant-danger,
.variant-warning,
.variant-success {
  --holo-button-shadow: 0 3px 1.5px rgba(26, 26, 26, 0.3);

  color: var(--color-text-default, #fff);
  box-shadow: var(--holo-button-shadow);
}

.variant-danger {
  background: var(--color-danger-default, #fc1e1e);
}

.variant-danger:hover:not(.disabled) {
  background: var(--color-danger-hover, #ff3e3e);
}

.variant-danger:active:not(.disabled) {
  background: var(--color-danger-active, #e50f0f);
}

.variant-warning {
  background: var(--warning-color, #fca41e);
}

.variant-warning:hover:not(.disabled) {
  background: var(--color-warning-hover, #ffb038);
}

.variant-warning:active:not(.disabled) {
  background: var(--color-warning-active, #e78f09);
}

.variant-success {
  background: var(--success-color, #00b42a);
}

.variant-success:hover:not(.disabled) {
  background: var(--color-success-hover, #1ec345);
}

.variant-success:active:not(.disabled) {
  background: var(--color-success-active, #009222);
}

.variant-text {
  min-width: auto;
  color: var(--color-primary-default, #7b7cff);
  background: transparent;
}

.variant-text:hover:not(.disabled) {
  color: var(--color-primary-hover, #96a1ff);
}

.variant-text:active:not(.disabled) {
  color: var(--color-primary-active, #686aff);
}

.disabled.variant-primary,
.disabled.variant-icon {
  color: var(--color-text-disable, rgba(255, 255, 255, 0.3));
  background: var(--color-primary-disable, rgba(123, 124, 255, 0.3));
  box-shadow: 0 3px 3px rgba(26, 26, 26, 0.3);
}

.disabled.variant-ghost {
  color: var(--color-text-disable, rgba(255, 255, 255, 0.3));
  background: rgba(26, 35, 67, 0.3);
}

.disabled.variant-outline {
  color: var(--color-text-disable, rgba(255, 255, 255, 0.3));
  border-color: var(--color-primary-disable, rgba(123, 124, 255, 0.3));
}

.disabled.variant-danger {
  color: var(--color-text-disable, rgba(255, 255, 255, 0.3));
  background: var(--color-danger-disable, rgba(252, 30, 30, 0.3));
}

.disabled.variant-warning {
  color: var(--color-text-disable, rgba(255, 255, 255, 0.3));
  background: var(--color-warning-disable, rgba(252, 164, 30, 0.3));
}

.disabled.variant-success {
  color: var(--color-text-disable, rgba(255, 255, 255, 0.3));
  background: var(--color-success-disable, rgba(0, 180, 42, 0.3));
}

.disabled.variant-text {
  color: var(--color-text-disable, rgba(255, 255, 255, 0.3));
}

@keyframes holo-button-spin {
  to {
    transform: rotate(360deg);
  }
}
